import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Article as ArticleIcon,
  ViewModule as ContentIcon,
  Category as CategoryIcon,
  LocalOffer as OfferIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';

// Mock Chart Component
const MockChart = ({ title, type = 'line', height = 300 }) => {
  const generateRandomData = () => {
    return Array.from({ length: 12 }, () => Math.floor(Math.random() * 100));
  };

  const data = generateRandomData();
  const maxValue = Math.max(...data);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ height: height, display: 'flex', alignItems: 'end', gap: 1, p: 2 }}>
          {type === 'bar' ? (
            data.map((value, index) => (
              <Box
                key={index}
                sx={{
                  height: `${(value / maxValue) * 100}%`,
                  width: '100%',
                  backgroundColor: 'primary.main',
                  opacity: 0.7,
                  borderRadius: '4px 4px 0 0',
                  minHeight: '4px'
                }}
              />
            ))
          ) : (
            <svg width="100%" height="100%" viewBox="0 0 300 200">
              <polyline
                fill="none"
                stroke="#3B82F6"
                strokeWidth="3"
                points={data.map((value, index) => 
                  `${(index / (data.length - 1)) * 300},${200 - (value / maxValue) * 180}`
                ).join(' ')}
              />
              {data.map((value, index) => (
                <circle
                  key={index}
                  cx={(index / (data.length - 1)) * 300}
                  cy={200 - (value / maxValue) * 180}
                  r="4"
                  fill="#3B82F6"
                />
              ))}
            </svg>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Mock Pie Chart Component
const MockPieChart = ({ title, data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ position: 'relative', width: 200, height: 200 }}>
            <svg width="200" height="200" viewBox="0 0 200 200">
              {data.map((item, index) => {
                const percentage = (item.value / total) * 100;
                const angle = (item.value / total) * 360;
                const startAngle = currentAngle;
                const endAngle = currentAngle + angle;
                currentAngle += angle;

                const x1 = 100 + 80 * Math.cos((startAngle - 90) * Math.PI / 180);
                const y1 = 100 + 80 * Math.sin((startAngle - 90) * Math.PI / 180);
                const x2 = 100 + 80 * Math.cos((endAngle - 90) * Math.PI / 180);
                const y2 = 100 + 80 * Math.sin((endAngle - 90) * Math.PI / 180);
                const largeArc = angle > 180 ? 1 : 0;

                return (
                  <path
                    key={index}
                    d={`M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`}
                    fill={item.color}
                    opacity="0.8"
                  />
                );
              })}
            </svg>
          </Box>
          <Box>
            {data.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: item.color,
                    borderRadius: '2px'
                  }}
                />
                <Typography variant="body2">
                  {item.label}: {((item.value / total) * 100).toFixed(1)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Reporting = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Mock data for different stats
  const offerStatsData = {
    totalOffers: 156,
    activeOffers: 124,
    expiredOffers: 32,
    avgClickRate: 12.4,
    topOffers: [
      { name: 'Summer Sale 2024', clicks: 1200, conversions: 145 },
      { name: 'Black Friday Deal', clicks: 980, conversions: 120 },
      { name: 'Spring Collection', clicks: 756, conversions: 89 },
      { name: 'Holiday Special', clicks: 632, conversions: 76 },
      { name: 'Flash Sale', clicks: 445, conversions: 54 }
    ]
  };

  const affiliateStatsData = {
    totalAffiliates: 89,
    activeAffiliates: 67,
    topPerformers: [
      { name: 'john@email.com', commissions: '$2,450', conversions: 45 },
      { name: 'sarah@email.com', commissions: '$1,890', conversions: 38 },
      { name: 'mike@email.com', commissions: '$1,650', conversions: 32 },
      { name: 'lisa@email.com', commissions: '$1,320', conversions: 28 },
      { name: 'alex@email.com', commissions: '$980', conversions: 22 }
    ]
  };

  const blogStatsData = {
    totalPosts: 45,
    publishedPosts: 42,
    draftPosts: 3,
    avgViews: 2340,
    topPosts: [
      { title: '10 Tips for Better Marketing', views: 12500, engagement: '8.5%' },
      { title: 'Social Media Trends 2024', views: 9800, engagement: '7.2%' },
      { title: 'Email Marketing Guide', views: 8600, engagement: '9.1%' },
      { title: 'Content Strategy Basics', views: 7200, engagement: '6.8%' },
      { title: 'SEO Best Practices', views: 6800, engagement: '7.9%' }
    ]
  };

  const pieChartData = [
    { label: 'Active', value: 65, color: '#4caf50' },
    { label: 'Expired', value: 20, color: '#f44336' },
    { label: 'Draft', value: 10, color: '#ff9800' },
    { label: 'Pending', value: 5, color: '#9e9e9e' }
  ];

  const categoryData = [
    { label: 'Technology', value: 35, color: '#3B82F6' },
    { label: 'Fashion', value: 25, color: '#eabbc3' },
    { label: 'Health', value: 20, color: '#4caf50' },
    { label: 'Travel', value: 12, color: '#ff9800' },
    { label: 'Food', value: 8, color: '#9c27b0' }
  ];

  const renderOfferStats = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <OfferIcon color="primary" />
                <Box>
                  <Typography variant="h4">{offerStatsData.totalOffers}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Offers</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="success" />
                <Box>
                  <Typography variant="h4">{offerStatsData.activeOffers}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Offers</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="warning" />
                <Box>
                  <Typography variant="h4">{offerStatsData.expiredOffers}</Typography>
                  <Typography variant="body2" color="text.secondary">Expired Offers</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="info" />
                <Box>
                  <Typography variant="h4">{offerStatsData.avgClickRate}%</Typography>
                  <Typography variant="body2" color="text.secondary">Avg Click Rate</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <MockChart title="Offer Performance Over Time" type="line" />
        </Grid>
        <Grid item xs={12} md={4}>
          <MockPieChart title="Offer Status Distribution" data={pieChartData} />
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top Performing Offers</Typography>
              <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Offer Name</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Clicks</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Conversions</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Conversion Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {offerStatsData.topOffers.map((offer, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          '&:nth-of-type(even)': { backgroundColor: 'grey.25' },
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>{offer.name}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>{offer.clicks.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>{offer.conversions}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          {((offer.conversions / offer.clicks) * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderAffiliateStats = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupIcon color="primary" />
                <Box>
                  <Typography variant="h4">{affiliateStatsData.totalAffiliates}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Affiliates</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="success" />
                <Box>
                  <Typography variant="h4">{affiliateStatsData.activeAffiliates}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Affiliates</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="info" />
                <Box>
                  <Typography variant="h4">$12,450</Typography>
                  <Typography variant="body2" color="text.secondary">Total Commissions</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="warning" />
                <Box>
                  <Typography variant="h4">238</Typography>
                  <Typography variant="body2" color="text.secondary">Total Conversions</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MockChart title="Affiliate Signups Over Time" type="bar" />
        </Grid>
        <Grid item xs={12} md={6}>
          <MockChart title="Commission Trends" type="line" />
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top Performing Affiliates</Typography>
              <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Affiliate</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Commissions</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Conversions</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {affiliateStatsData.topPerformers.map((affiliate, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          '&:nth-of-type(even)': { backgroundColor: 'grey.25' },
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>{affiliate.name}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>{affiliate.commissions}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>{affiliate.conversions}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Chip label="Active" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderBlogStats = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ArticleIcon color="primary" />
                <Box>
                  <Typography variant="h4">{blogStatsData.totalPosts}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Posts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="success" />
                <Box>
                  <Typography variant="h4">{blogStatsData.publishedPosts}</Typography>
                  <Typography variant="body2" color="text.secondary">Published</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="warning" />
                <Box>
                  <Typography variant="h4">{blogStatsData.draftPosts}</Typography>
                  <Typography variant="body2" color="text.secondary">Drafts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="info" />
                <Box>
                  <Typography variant="h4">{blogStatsData.avgViews.toLocaleString()}</Typography>
                  <Typography variant="body2" color="text.secondary">Avg Views</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <MockChart title="Blog Views Over Time" type="line" />
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Engagement Rate</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Average: 7.5%</Typography>
                <LinearProgress variant="determinate" value={75} sx={{ mt: 1 }} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">This Month: 8.2%</Typography>
                <LinearProgress variant="determinate" value={82} sx={{ mt: 1 }} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Goal: 10%</Typography>
                <LinearProgress variant="determinate" value={100} sx={{ mt: 1 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top Blog Posts</Typography>
              <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Post Title</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Views</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Engagement Rate</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {blogStatsData.topPosts.map((post, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          '&:nth-of-type(even)': { backgroundColor: 'grey.25' },
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>{post.title}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>{post.views.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>{post.engagement}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Chip label="Published" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderContentBlockStats = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ContentIcon color="primary" />
                <Box>
                  <Typography variant="h4">245</Typography>
                  <Typography variant="body2" color="text.secondary">Total Blocks</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="success" />
                <Box>
                  <Typography variant="h4">189</Typography>
                  <Typography variant="body2" color="text.secondary">Active Blocks</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="info" />
                <Box>
                  <Typography variant="h4">15.2K</Typography>
                  <Typography variant="body2" color="text.secondary">Total Views</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="warning" />
                <Box>
                  <Typography variant="h4">4.8%</Typography>
                  <Typography variant="body2" color="text.secondary">Click Rate</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MockChart title="Content Block Performance" type="bar" />
        </Grid>
        <Grid item xs={12} md={6}>
          <MockChart title="Block Interactions" type="line" />
        </Grid>
      </Grid>
    </Box>
  );

  const renderCategoryStats = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CategoryIcon color="primary" />
                <Box>
                  <Typography variant="h4">28</Typography>
                  <Typography variant="body2" color="text.secondary">Total Categories</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="success" />
                <Box>
                  <Typography variant="h4">24</Typography>
                  <Typography variant="body2" color="text.secondary">Active Categories</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="info" />
                <Box>
                  <Typography variant="h4">156</Typography>
                  <Typography variant="body2" color="text.secondary">Total Offers</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="warning" />
                <Box>
                  <Typography variant="h4">5.6</Typography>
                  <Typography variant="body2" color="text.secondary">Avg Offers/Category</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <MockChart title="Category Performance" type="bar" />
        </Grid>
        <Grid item xs={12} md={4}>
          <MockPieChart title="Category Distribution" data={categoryData} />
        </Grid>
      </Grid>
    </Box>
  );

  const tabContent = [
    renderOfferStats,
    renderAffiliateStats,
    renderBlogStats,
    renderContentBlockStats,
    renderCategoryStats
  ];

  return (
    <DashboardLayout title="Reporting">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Comprehensive analytics and reporting dashboard
          </Typography>
        </Box>

        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Offer Stats" />
            <Tab label="Affiliate Stats" />
            <Tab label="Blog Stats" />
            <Tab label="Content Block Stats" />
            <Tab label="Category Stats" />
          </Tabs>
        </Paper>

        <Box sx={{ py: 3 }}>
          {tabContent[activeTab]()}
        </Box>
      </Container>
    </DashboardLayout>
  );
};

export default Reporting; 